/**
 * CASE SHELL CANON — one card per controller (header + risk tabs + hit panel).
 *
 * KB: mira-safe/риски-ui-таблица-и-мёртвые.
 * Full contract: `./riskCardLayoutCanon.js`.
 *
 * HARD RULES
 * - New risks are tabs inside THIS card, not new card components.
 * - Active tab body MUST be RiskHitPanelLayout (via RiskCard / RiskNHitPanel).
 * - Multi-risk filter must NOT spawn multiple cards — flatten is table-only.
 * - Table ℹ uses RiskCaseDetailModal → this same RiskCaseCard (1:1).
 * - Outer size locked by RISK_CASE_CARD; slotted content must not grow it.
 */
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { displayIntegrationLabel } from '../constants/integrationLabels';
import { getRiskLevelShortLabel, getRiskShortLabel } from '../constants/riskTypes';
import { resolveSupportActionLabel } from '../constants/supportActionLabels';
import { buildDeviceHistoryPath } from '../pages/deviceHistoryPath';
import CommentsNotesButton from './CommentsNotesButton';
import RiskCard, { formatDateTime } from './RiskCard';
import RiskHitPanelLayout, {
  RISK_HIT_LAYOUT,
  RiskHitMetricGrid,
} from './RiskHitPanelLayout';
import { RISK_CARD_LAYOUT_CANON } from './riskCardLayoutCanon';
import TransmissionScalePlaceholder from './TransmissionScalePlaceholder';
import TransmissionScheduleTimeline from './TransmissionScheduleTimeline';
import Risk3HitPanel from './Risk3HitPanel';
import Risk4HitPanel from './Risk4HitPanel';
import {
  buildCaseTabSlots,
  buildSlotTabKey,
  pickDefaultSlotKey as pickDefaultSlotKeyFromSlots,
} from './riskCaseTabSlots';

void RISK_CARD_LAYOUT_CANON;

/** Outer case card shell — fixed box; content cannot change card size. */
export const RISK_CASE_CARD = Object.freeze({
  root:
    'flex h-full w-full min-h-[40rem] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/60 p-3.5 shadow-lg shadow-slate-200/70 dark:shadow-slate-950/20',
  header: 'mb-2.5 flex shrink-0 items-start gap-2',
  headerMain: 'min-w-0 flex-1 space-y-1.5 overflow-hidden',
  panel: 'flex min-h-0 flex-1 flex-col overflow-hidden',
});

export { buildCaseTabSlots, buildEmptyTabKey, buildSlotTabKey } from './riskCaseTabSlots';

const FAILURE_REASON_LABELS = {
  mosru_autotransfer_blocked: 'Автопередача заблокирована',
  controller_offline: 'Контроллер без связи',
  integration_error: 'Ошибка интеграции',
  missed_period_send: 'Пропущена передача',
};

const INTEGRATION_CATEGORY_LABELS = {
  company_transmission: 'Передача в компанию',
  user_readings_delivery: 'Передача пользователем',
};

function shortCounterSerial(serial) {
  if (!serial) {
    return null;
  }

  return serial.length > 8 ? serial.slice(-8) : serial;
}

/** Hide upstream Go/runtime noise (e.g. strconv.Atoi) — failure_reason already covers it. */
function isOperatorFacingMessage(message) {
  if (!message || !String(message).trim()) {
    return false;
  }

  const text = String(message).trim();
  if (/^strconv\./i.test(text)) {
    return false;
  }
  if (/^runtime error:/i.test(text)) {
    return false;
  }
  if (/^panic:/i.test(text)) {
    return false;
  }
  if (/parsing\s+"":\s*invalid syntax/i.test(text)) {
    return false;
  }

  return true;
}

/** Active tab: name · level [· serial]. Inactive: compact `#N · level`. */
function buildHitTabLabel(hit, sameRiskHits, { compact = false } = {}) {
  const shortLevel = getRiskLevelShortLabel(hit.rule_result?.risk_level);

  if (compact) {
    return `#${hit.risk_number ?? '?'} · ${shortLevel}`;
  }

  const shortName = getRiskShortLabel(hit.risk_number);
  const duplicateCounters =
    sameRiskHits.filter((item) => item.risk_number === hit.risk_number).length > 1;
  const counterSerial = shortCounterSerial(hit.facts?.counter_serial_number || hit.facts?.serial_number);

  if (duplicateCounters && counterSerial) {
    return `${shortName} · ${shortLevel} · ${counterSerial}`;
  }

  return `${shortName} · ${shortLevel}`;
}

function buildEmptyTabLabel(riskNumber, { compact = false } = {}) {
  if (compact) {
    return `#${riskNumber} · —`;
  }
  return `${getRiskShortLabel(riskNumber)} · —`;
}

function pickDefaultHitIndex(hits) {
  if (hits.length === 0) {
    return 0;
  }

  let bestIndex = 0;
  let bestScore = hits[0].rule_result?.risk_score;

  hits.forEach((hit, index) => {
    const score = hit.rule_result?.risk_score;
    if (score == null) return;
    if (bestScore == null || score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestIndex;
}

export function pickDefaultSlotKey(slots) {
  return pickDefaultSlotKeyFromSlots(slots, pickDefaultHitIndex);
}

const RISK2_METRIC_HINTS = {
  failureReason: 'Почему интеграция считает передачу показаний сорванной.',
  overdue: 'С момента первого пропущенного слота по графику (день, неделя или месяц).',
  missedTransmissionWindows:
    'Сколько наступивших моментов передачи из настройки интеграции пропущено с начала сбоя связи (пока устройство всё ещё overdue).',
};

/**
 * Risk #2 panel — fills RiskHitPanelLayout slots only.
 */
function Risk2HitPanel({ hit, onOpenHistory }) {
  const { facts, features, rule_result: ruleResult } = hit;
  const riskScore =
    ruleResult.risk_score == null || !Number.isFinite(Number(ruleResult.risk_score))
      ? null
      : Number(ruleResult.risk_score);
  const riskLevel = ruleResult.risk_level ?? (riskScore == null ? 'unknown' : ruleResult.risk_level);
  const failureReason = features.failure_reason;
  const scheduleLabel =
    features.transmission_schedule_label ||
    (features.send_at_day != null || facts.send_at_day != null
      ? `${features.send_at_day ?? facts.send_at_day}-е число месяца (MSK)`
      : null);
  const messageText = isOperatorFacingMessage(facts.message) ? String(facts.message).trim() : '';
  const counterSerial = facts.counter_serial_number || facts.serial_number || 'Без serial';
  const deviceId = facts.counter_id ?? facts.device_id;

  const criticalRows = [
    {
      key: 'action',
      label: 'Действие',
      value: resolveSupportActionLabel(ruleResult),
    },
    {
      key: 'failure-reason',
      label: 'Причина сбоя',
      hint: RISK2_METRIC_HINTS.failureReason,
      value: FAILURE_REASON_LABELS[failureReason] || failureReason || '—',
    },
    {
      key: 'overdue',
      label: 'Просрочка',
      hint: RISK2_METRIC_HINTS.overdue,
      value: `${features.days_overdue ?? 0} дн.`,
      numeric: true,
    },
    {
      key: 'missed-transmission',
      label: 'Пропущено передач',
      hint: RISK2_METRIC_HINTS.missedTransmissionWindows,
      value: features.missed_transmission_periods ?? features.missed_transmission_windows ?? 0,
      numeric: true,
    },
  ];

  const detailRows = [
    {
      key: 'channel',
      label: 'Канал',
      value:
        INTEGRATION_CATEGORY_LABELS[features.integration_category] ||
        facts.integration_name ||
        facts.integration_key ||
        '—',
    },
    {
      key: 'schedule',
      label: 'График передачи',
      value: scheduleLabel || 'не указан',
    },
    {
      key: 'status',
      label: 'Статус',
      value: facts.status_key || '—',
    },
  ];

  const hasScheduleAnchor =
    features.send_at_day != null ||
    facts.send_at_day != null ||
    features.transmission_instant ||
    ruleResult.deadline_at;
  const showTimeline = hasScheduleAnchor && features.failure_at;

  return (
    <RiskHitPanelLayout
      embedded
      riskNumber={2}
      badges={
        <>
          <span className="inline-flex shrink-0 rounded-full border border-slate-300 dark:border-slate-600/50 bg-slate-100 dark:bg-slate-800/80 px-2 py-0.5 text-xs font-medium text-slate-700 dark:text-slate-300">
            Счётчик
          </span>
          {facts.integration_key && (
            <span className="inline-flex shrink-0 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2 py-0.5 text-xs font-medium text-cyan-700 dark:text-cyan-200">
              {displayIntegrationLabel(facts.integration_key, facts.integration_name)}
            </span>
          )}
          {failureReason === 'mosru_autotransfer_blocked' && (
            <span className="inline-flex shrink-0 rounded-full border border-red-400/30 bg-red-400/10 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-200">
              {FAILURE_REASON_LABELS[failureReason]}
            </span>
          )}
          {facts.hot_water_unused && (
            <span className="inline-flex shrink-0 rounded-full border border-sky-400/40 bg-sky-400/10 px-2 py-0.5 text-xs font-medium text-sky-800 dark:text-sky-200">
              ГВС не использует
            </span>
          )}
        </>
      }
      title={counterSerial}
      scoreDisplay={riskScore == null ? '—' : riskScore.toFixed(1)}
      riskLevel={riskLevel}
      scoreBarValue={riskScore ?? 0}
      feature={
        showTimeline ? (
          <TransmissionScheduleTimeline
            failureAt={features.failure_at}
            transmissionAt={features.transmission_instant || ruleResult.deadline_at}
            scheduleLabel={scheduleLabel}
            missedPeriods={features.missed_transmission_periods ?? features.missed_transmission_windows}
            sendAtDay={features.send_at_day ?? facts.send_at_day}
            sendAtHour={features.send_at_hour}
          />
        ) : (
          <TransmissionScalePlaceholder features={features} facts={facts} hasScheduleAnchor={Boolean(hasScheduleAnchor)} />
        )
      }
      metrics={<RiskHitMetricGrid rows={criticalRows} />}
      details={
        <>
          {detailRows.length > 0 && <RiskHitMetricGrid rows={detailRows} />}
          <div className="space-y-2 text-sm">
            <p>
              <span className="text-slate-500">Сбой с:</span> {formatDateTime(features.failure_at)}
            </p>
            <p>
              <span className="text-slate-500">Последняя передача:</span> {formatDateTime(features.sent_at)}
            </p>
            <p>
              <span className="text-slate-500">Дедлайн:</span> {formatDateTime(ruleResult.deadline_at)}
            </p>
            {messageText ? (
              <p>
                <span className="text-slate-500">Сообщение:</span> {messageText}
              </p>
            ) : null}
          </div>
          {ruleResult.reason_codes?.length > 0 && (
            <div>
              <p className="mb-1.5 text-xs text-slate-500">Причины решения</p>
              <div className="flex flex-wrap gap-1.5">
                {ruleResult.reason_codes.map((code) => (
                  <span
                    key={code}
                    className="rounded-full bg-white px-2 py-1 text-xs text-slate-500 dark:bg-slate-900 dark:text-slate-400"
                  >
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      }
      actions={
        deviceId != null ? (
          <>
            {onOpenHistory && (
              <button
                type="button"
                onClick={() =>
                  onOpenHistory({
                    ...facts,
                    device_id: deviceId,
                    serial_number: counterSerial,
                    risk_number: 2,
                    integration_id: facts.integration_id,
                  })
                }
                className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionHistory}`}
              >
                История рисков
              </button>
            )}
            <Link
              to={buildDeviceHistoryPath(deviceId, { serialNumber: counterSerial })}
              className={`${RISK_HIT_LAYOUT.actionBtn} ${RISK_HIT_LAYOUT.actionSecondary}`}
            >
              История устройства
            </Link>
          </>
        ) : null
      }
    />
  );
}

function CaseHeaderPath({ header, organizationLabel }) {
  const [open, setOpen] = useState(false);
  const [box, setBox] = useState(null);
  const rootRef = useRef(null);
  const popRef = useRef(null);
  const hideTimerRef = useRef(null);
  const rows = [
    {
      key: 'org',
      text: organizationLabel || (header.organization_id != null ? `УК #${header.organization_id}` : ''),
      className: 'text-slate-500 dark:text-slate-400',
    },
    {
      key: 'name',
      text: header.user_full_name || (header.user_id != null ? `User #${header.user_id}` : ''),
      className: 'font-medium text-slate-900 dark:text-white',
    },
    {
      key: 'address',
      text: header.address || '',
      className: 'text-slate-500 dark:text-slate-400',
    },
    {
      key: 'serial',
      text: header.controller_serial || '',
      className: 'text-slate-500 dark:text-slate-400',
    },
  ];
  const fullPath = rows.map((row) => row.text).filter(Boolean).join(' › ');

  const showPopup = () => {
    window.clearTimeout(hideTimerRef.current);
    setOpen(true);
  };

  const hidePopup = () => {
    window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => setOpen(false), 120);
  };

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    const place = () => {
      const el = rootRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      setBox({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, fullPath]);

  useEffect(() => () => window.clearTimeout(hideTimerRef.current), []);

  return (
    <div
      ref={rootRef}
      className="grid h-16 min-w-0 grid-rows-4"
      onMouseEnter={showPopup}
      onMouseLeave={hidePopup}
    >
      {rows.map((row) => (
        <p key={row.key} className={`block h-4 truncate text-xs leading-4 ${row.className}`}>
          {row.text || '\u00a0'}
        </p>
      ))}
      {open && box && fullPath && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popRef}
              role="tooltip"
              style={{ top: box.top, left: box.left, width: box.width }}
              onMouseEnter={showPopup}
              onMouseLeave={hidePopup}
              className="fixed z-[80] rounded-md bg-white px-2 py-1.5 text-xs leading-4 shadow-lg ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-700"
            >
              {rows.map((row) =>
                row.text ? (
                  <p key={row.key} className={`break-words ${row.className}`}>
                    {row.text}
                  </p>
                ) : null,
              )}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function CaseHeaderContacts({ header }) {
  if (!header.user_phone) {
    return null;
  }

  // Address + serial live in the path chain; keep only phone here to avoid duplication.
  return <p className="truncate text-sm text-slate-700 dark:text-slate-300">{header.user_phone}</p>;
}

/**
 * Compact header tabs: wrap instead of scroll so cards never get an inner scrollbar.
 * Height stays roughly one/two lines so the card grid can stretch evenly.
 */
function CaseHeaderTabs({ slots, activeTabKey, onSelect, tablistId, panelId }) {
  const keys = slots.map((slot) => buildSlotTabKey(slot));
  const hitList = slots.filter((slot) => !slot.empty && slot.hit).map((slot) => slot.hit);
  const activeIndex = Math.max(
    0,
    keys.findIndex((key) => key === activeTabKey),
  );

  function focusTabAt(index) {
    const next = (index + slots.length) % slots.length;
    onSelect(keys[next]);
    requestAnimationFrame(() => {
      document.getElementById(`${tablistId}-tab-${next}`)?.focus();
    });
  }

  function onKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusTabAt(activeIndex + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusTabAt(activeIndex - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      focusTabAt(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      focusTabAt(slots.length - 1);
    }
  }

  return (
    <div
      id={tablistId}
      role="tablist"
      aria-label="Риски кейса"
      onKeyDown={onKeyDown}
      className="flex max-w-full min-w-0 flex-wrap items-center gap-1"
    >
      {slots.map((slot, index) => {
        const tabKey = keys[index];
        const isActive = tabKey === activeTabKey;
        const label = slot.empty
          ? buildEmptyTabLabel(slot.riskNumber, { compact: !isActive })
          : buildHitTabLabel(slot.hit, hitList, { compact: !isActive });
        const title = slot.empty
          ? buildEmptyTabLabel(slot.riskNumber, { compact: false })
          : buildHitTabLabel(slot.hit, hitList, { compact: false });

        return (
          <button
            key={tabKey}
            id={`${tablistId}-tab-${index}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={panelId}
            tabIndex={isActive ? 0 : -1}
            title={title}
            onClick={() => onSelect(tabKey)}
            className={`h-7 shrink-0 whitespace-nowrap rounded-md border px-2 text-[11px] font-medium leading-none transition ${
              isActive
                ? slot.empty
                  ? 'border-slate-400/50 bg-slate-100 text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300'
                  : 'border-sky-400/40 bg-sky-400/10 text-sky-800 dark:text-sky-100'
                : slot.empty
                  ? 'border-slate-200 bg-transparent text-slate-400 hover:border-slate-300 hover:text-slate-500 dark:border-slate-800 dark:text-slate-500 dark:hover:border-slate-700 dark:hover:text-slate-400'
                  : 'border-slate-300 dark:border-slate-700/80 bg-slate-50 dark:bg-slate-900/40 text-slate-500 hover:border-slate-400 dark:hover:border-slate-600 hover:text-slate-800 dark:hover:text-slate-300'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyRiskPanel({ riskNumber }) {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col justify-center overflow-hidden rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-3 py-6 text-center dark:border-slate-800 dark:bg-slate-900/30">
      <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
        #{riskNumber} · {getRiskShortLabel(riskNumber)}
      </p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Нет срабатывания по этому риску
      </p>
    </div>
  );
}

export default function RiskCaseCard({
  riskCase,
  organizationLabel,
  onOpenClientLk,
  openingUserId,
  onOpenHistory,
  commentsOpen = false,
  onToggleComments,
  /** Open on this risk tab when provided (e.g. table ℹ from a #2/#3/#4 row). */
  initialRiskNumber = null,
}) {
  const { header, hits } = riskCase;
  const tablistId = useId();
  const slots = useMemo(() => buildCaseTabSlots(hits), [hits]);
  const defaultTabKey = useMemo(() => {
    const preferred =
      initialRiskNumber != null && Number.isFinite(Number(initialRiskNumber))
        ? Number(initialRiskNumber)
        : null;
    if (preferred != null) {
      const match = slots.find((slot) => !slot.empty && Number(slot.riskNumber) === preferred);
      if (match) return buildSlotTabKey(match);
    }
    return pickDefaultSlotKey(slots);
  }, [slots, initialRiskNumber]);
  const [activeTabKey, setActiveTabKey] = useState(defaultTabKey);

  useEffect(() => {
    setActiveTabKey(defaultTabKey);
  }, [defaultTabKey]);

  useEffect(() => {
    if (!slots.some((slot) => buildSlotTabKey(slot) === activeTabKey)) {
      setActiveTabKey(defaultTabKey);
    }
  }, [slots, activeTabKey, defaultTabKey]);

  const activeSlot =
    slots.find((slot) => buildSlotTabKey(slot) === activeTabKey) || slots[0] || null;
  const activeHit = activeSlot && !activeSlot.empty ? activeSlot.hit : null;
  const activeTabIndex = Math.max(
    0,
    slots.findIndex((slot) => buildSlotTabKey(slot) === buildSlotTabKey(activeSlot)),
  );
  const panelId = `${tablistId}-panel`;

  return (
    <article className={RISK_CASE_CARD.root} data-risk-case-card="canon">
      {/* One header: identity + tabs + LK — size locked by RISK_CASE_CARD */}
      <div className={RISK_CASE_CARD.header}>
        <div className={RISK_CASE_CARD.headerMain}>
          <CaseHeaderPath header={header} organizationLabel={organizationLabel} />
          <CaseHeaderContacts header={header} />
          {slots.length > 0 ? (
            <CaseHeaderTabs
              slots={slots}
              activeTabKey={buildSlotTabKey(activeSlot)}
              onSelect={setActiveTabKey}
              tablistId={tablistId}
              panelId={panelId}
            />
          ) : null}
        </div>

        <div className="flex shrink-0 items-start gap-1.5">
          {onToggleComments ? (
            <CommentsNotesButton
              open={commentsOpen}
              size="lg"
              onClick={() => onToggleComments(riskCase)}
            />
          ) : null}
          {header.user_id && header.lk_client_available && onOpenClientLk && (
            <button
              type="button"
              onClick={() => onOpenClientLk(header.user_id)}
              disabled={openingUserId === header.user_id}
              className="shrink-0 rounded-xl border border-sky-400/30 bg-sky-400/10 px-3 py-2 text-sm font-medium text-sky-700 dark:text-sky-200 transition hover:border-sky-400/50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {openingUserId === header.user_id ? 'ЛК...' : 'ЛК'}
            </button>
          )}
        </div>
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={slots.length > 0 ? `${tablistId}-tab-${activeTabIndex}` : undefined}
        className={RISK_CASE_CARD.panel}
      >
        {activeSlot?.empty || !activeHit ? (
          <EmptyRiskPanel riskNumber={activeSlot?.riskNumber ?? '?'} />
        ) : activeHit.risk_number === 2 ? (
          <Risk2HitPanel hit={activeHit} onOpenHistory={onOpenHistory} />
        ) : activeHit.risk_number === 3 ? (
          <Risk3HitPanel hit={activeHit} onOpenHistory={onOpenHistory} />
        ) : activeHit.risk_number === 4 ? (
          <Risk4HitPanel hit={activeHit} onOpenHistory={onOpenHistory} />
        ) : (
          <RiskCard
            item={activeHit}
            onOpenClientLk={onOpenClientLk}
            openingUserId={openingUserId}
            onOpenHistory={onOpenHistory}
            hideFio
            hideContactsInAccordion
            hideLk
            embedded
          />
        )}
      </div>
    </article>
  );
}
